import ActionSuccessToast from '../ActionSuccessToast'
import {useEffect,useRef,useState} from 'react'
import {createPortal} from 'react-dom'
import {Sun,Moon} from 'lucide-react'
import {useInterfaceSaturation} from '../../hooks/useInterfaceSaturation.js'

const chartSelector='.tv-lightweight-charts,.recharts-wrapper,canvas,[data-preserve-color],.asset-info-price,.asset-info-change'

// A compositing layer tones the surrounding interface without filtering chart
// ancestors (which would also change chart colors and fixed-position geometry).
function ColorLayer({value}){
 const [clip,setClip]=useState('')
 useEffect(()=>{
  if(value===100)return
  let frame=0,observed=[]
  const resize=new ResizeObserver(()=>schedule())
  const update=()=>{
   frame=0
   const width=innerWidth,height=innerHeight
   const charts=[...document.querySelectorAll(chartSelector)].filter(el=>!el.parentElement?.closest(chartSelector))
   if(charts.length!==observed.length||charts.some((el,i)=>el!==observed[i])){
    resize.disconnect();resize.observe(document.documentElement);charts.forEach(el=>resize.observe(el));observed=charts
   }
   let path=`M0 0H${width}V${height}H0Z`
   for(const el of charts){
    const r=el.getBoundingClientRect();let left=Math.max(0,r.left),top=Math.max(0,r.top),right=Math.min(width,r.right),bottom=Math.min(height,r.bottom)
    for(let p=el.parentElement;p&&p!==document.body;p=p.parentElement){
     const css=getComputedStyle(p),box=p.getBoundingClientRect()
     if(/hidden|clip|auto|scroll/.test(css.overflowX)){left=Math.max(left,box.left);right=Math.min(right,box.right)}
     if(/hidden|clip|auto|scroll/.test(css.overflowY)){top=Math.max(top,box.top);bottom=Math.min(bottom,box.bottom)}
    }
    if(right>left&&bottom>top)path+=`M${left} ${top}H${right}V${bottom}H${left}Z`
   }
   setClip(`path(evenodd, "${path}")`)
  }
  function schedule(){if(!frame)frame=requestAnimationFrame(update)}
  const mutations=new MutationObserver(schedule)
  mutations.observe(document.body,{childList:true,subtree:true})
  window.addEventListener('resize',schedule);window.addEventListener('scroll',schedule,true)
  schedule()
  return()=>{cancelAnimationFrame(frame);resize.disconnect();mutations.disconnect();window.removeEventListener('resize',schedule);window.removeEventListener('scroll',schedule,true)}
 },[value===100])
 if(value===100)return null
 return createPortal(<div data-testid="interface-color-layer" aria-hidden="true" style={{position:'fixed',inset:0,pointerEvents:'none',zIndex:2147483646,backdropFilter:`saturate(${value/100})`,WebkitBackdropFilter:`saturate(${value/100})`,clipPath:clip||'inset(100%)'}}/>,document.body)
}

export default function InterfaceColorControl({dark,onToggle,userId}){
 const {values,status,change:save,retry}=useInterfaceSaturation(userId)
 const [anchor,setAnchor]=useState(null),button=useRef(null),panel=useRef(null)
 const mode=dark?'dark':'light',value=values[mode]
 const [saveNotice,setSaveNotice]=useState(''),previousStatus=useRef(null)
 useEffect(()=>{
  if(previousStatus.current?.mode===mode && previousStatus.current?.state==='saving' && status[mode]==='saved')setSaveNotice('Interface colors saved')
  previousStatus.current={mode,state:status[mode]}
 },[mode,status[mode]])
 useEffect(()=>{
  if(!anchor)return
  panel.current?.querySelector('input')?.focus()
  const outside=e=>{if(!panel.current?.contains(e.target)&&!button.current?.contains(e.target))setAnchor(null)}
  const escape=e=>{if(e.key==='Escape'){e.preventDefault();e.stopPropagation();setAnchor(null);button.current?.focus()}}
  document.addEventListener('pointerdown',outside);document.addEventListener('keydown',escape,true)
  const reposition=()=>{const r=button.current?.getBoundingClientRect();if(r)setAnchor({right:r.right,bottom:r.bottom})}
  window.addEventListener('resize',reposition)
  return()=>{document.removeEventListener('pointerdown',outside);document.removeEventListener('keydown',escape,true);window.removeEventListener('resize',reposition)}
 },[!!anchor])
 const change=n=>save(mode,n)
 const label=dark?'Switch to light mode':'Switch to dark mode'
 return <>
  <ActionSuccessToast message={saveNotice} onDismiss={()=>setSaveNotice('')} />
  <button ref={button} type="button" data-command-id="app.theme.toggle" data-command-label={label} aria-label={label} title="Switch theme · Command-click to adjust interface colors" aria-expanded={!!anchor}
   onClick={event=>{if(event.metaKey){event.preventDefault();const r=event.currentTarget.getBoundingClientRect();setAnchor(anchor?null:{right:r.right,bottom:r.bottom})}else onToggle()}}
   className="flex items-center justify-center w-8 h-8 rounded-full bg-canvas2 border border-border text-inkMuted hover:text-ink hover:border-ink/30 transition-colors">
   {dark?<Sun size={14}/>:<Moon size={14}/>}</button>
  <ColorLayer value={value}/>
  {anchor&&createPortal(<div ref={panel} role="dialog" aria-label="Interface color intensity" className="rounded-lg border border-border bg-canvas p-3 text-ink shadow-xl" style={{position:'fixed',zIndex:10000,width:264,maxWidth:'calc(100vw - 16px)',left:Math.max(8,Math.min(innerWidth-272,anchor.right-264)),top:Math.max(8,Math.min(innerHeight-164,anchor.bottom+8))}}>
   <div className="mb-3 flex items-center justify-between text-xs"><label htmlFor="interface-saturation">Interface color intensity</label><output htmlFor="interface-saturation" className="tabular-nums">{value}%</output></div>
   <input id="interface-saturation" type="range" disabled={!userId} min="0" max="100" step="1" value={value} onChange={e=>change(Number(e.target.value))} className="w-full"/>
   <div className="mt-1 flex justify-between text-[10px] text-inkMuted"><span>Muted</span><span>Full color</span></div>
   <p className="mt-2 text-[10px] text-inkMuted">Charts, prices and P&L keep their colors.</p>
   <p role="status" className="mt-1 text-[10px] text-inkMuted">{status[mode]==='error'?<>Not synced yet. <button type="button" onClick={retry} className="underline">Retry</button></>:status[mode]==='loading'?'Loading account setting…':status[mode]==='saving'?'Saving to your account…':status[mode]==='unavailable'?'Sign in to sync settings.':status[mode]==='ready'?`Account default · ${mode} mode`:`Account preference · ${mode} mode`}</p>
  </div>,document.body)}
 </>
}
